import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import EditIcon from "@mui/icons-material/Edit";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import { Box, type SxProps, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { SystemBackButton } from "../../../../components/UIToolkit/Navigation/SystemBackButton";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { INK_BLACK, MOTION, SURFACE } from "../../../../theme/tokens";
import { ProfileMode } from "../../../../utils/models";

type Props = {
	profileMode: ProfileMode;
	/** Already translated: "Back", or "Back to wage breakdown" when the profile was opened from one. */
	backLabel: string;
	onBackClick: () => void;
	onEditClick: () => void;
	onEditCancelClick: () => void;
	onEditSaveClick: () => void;
	onChatClick?: () => void;
	chatDisabled?: boolean;
	saveEnabled: boolean;
	saveLoading: boolean;
	onSyncPtoClick?: () => void;
	isSyncingPto?: boolean;
	canSyncPto?: boolean;
	nurseName?: string;
	/** Off while the profile's own header shows the name, so it is never on screen twice. */
	isNameVisible: boolean;
	/**
	 * The employee on screen is not yet the one the route asks for (a profile switch is in flight), so
	 * the actions that act *on* that employee would target the previous one. Back stays live — where it
	 * goes does not depend on who is loaded.
	 */
	recordActionsDisabled?: boolean;
};

const buttonSx = {
	width: "fit-content",
	fontSize: {
		xs: "14px",
		sm: "16px",
	},
} as const satisfies SxProps;

export const ProfileTopBar: FC<Props> = ({
	profileMode,
	backLabel,
	onEditCancelClick,
	onEditSaveClick,
	onEditClick,
	onBackClick,
	onChatClick,
	chatDisabled,
	saveEnabled,
	saveLoading,
	onSyncPtoClick,
	isSyncingPto,
	canSyncPto,
	nurseName,
	isNameVisible,
	recordActionsDisabled,
}) => {
	const { t } = useTranslation();
	const chatButton = onChatClick && (
		<SystemButton
			data-testid="profile-chat-button"
			sx={buttonSx}
			key={"ChatButton"}
			buttonVariant={"secondary"}
			leadingIcon={<ChatBubbleOutlineOutlinedIcon />}
			onClick={onChatClick}
			disabled={chatDisabled || recordActionsDisabled}
		>
			{t("send.message")}
		</SystemButton>
	);
	return (
		<Box
			data-testid="profile-top-bar"
			sx={{
				position: "relative",
				display: "flex",
				alignItems: "center",
				justifyContent: profileMode === ProfileMode.EDIT ? "end" : "space-between",
				width: "100%",
				background: SURFACE,
				padding: {
					xs: "8px 8px",
					md: "10px 20px",
				},
				color: INK_BLACK,
				boxShadow: (theme) => `0px 4px 8px 0px ${alpha(theme.palette.primary.dark, 0.15)}`,
				border: `1px solid ${alpha(INK_BLACK, 0.1)}`,
			}}
		>
			{profileMode !== ProfileMode.EDIT && <SystemBackButton dataTestId="profile-back-button" sx={buttonSx} label={backLabel} onClick={onBackClick} />}
			{nurseName && (
				<Typography
					data-testid="profile-top-bar-name"
					aria-hidden={!isNameVisible}
					sx={{
						opacity: isNameVisible ? 1 : 0,
						transition: `opacity ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
						position: "absolute",
						left: "50%",
						transform: "translateX(-50%)",
						fontWeight: 600,
						fontSize: { xs: "14px", sm: "16px" },
						color: "primary.main",
						maxWidth: "40%",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						pointerEvents: "none",
					}}
				>
					{nurseName}
				</Typography>
			)}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "8px",
				}}
			>
				{profileMode === ProfileMode.VIEW ? (
					<Fragment>
						{chatDisabled ? (
							<InHouseTooltip title={t("nurse.has.not.signed.in.to.staff.app")}>
								{/* span wrapper lets MUI tooltip work over a disabled button */}
								<span>{chatButton}</span>
							</InHouseTooltip>
						) : (
							chatButton
						)}
						{canSyncPto && onSyncPtoClick && (
							<SystemButton
								data-testid="profile-sync-pto-button"
								sx={buttonSx}
								key={"SyncPtoButton"}
								buttonVariant={"secondary"}
								leadingIcon={<SyncOutlinedIcon />}
								onClick={onSyncPtoClick}
								loading={isSyncingPto}
								disabled={isSyncingPto || recordActionsDisabled}
							>
								{t("pto.sync.nurse.button")}
							</SystemButton>
						)}
						<SystemButton
							data-testid="profile-edit-button"
							sx={buttonSx}
							key={"EditButton"}
							buttonVariant={"primary"}
							leadingIcon={<EditIcon />}
							onClick={onEditClick}
							disabled={recordActionsDisabled}
						>
							{t("edit")}
						</SystemButton>
					</Fragment>
				) : (
					<Fragment>
						{profileMode === ProfileMode.EDIT && (
							<SystemButton data-testid="profile-cancel-button" sx={buttonSx} key={"CancelButton"} buttonVariant={"secondary"} onClick={onEditCancelClick}>
								{t("cancel")}
							</SystemButton>
						)}
						<SystemButton
							data-testid="profile-save-button"
							sx={buttonSx}
							key={"SaveButton"}
							buttonVariant={"primary"}
							onClick={onEditSaveClick}
							disabled={!saveEnabled}
							loading={saveLoading}
						>
							{profileMode === ProfileMode.NEW ? t("create") : t("save.changes")}
						</SystemButton>
					</Fragment>
				)}
			</Box>
		</Box>
	);
};
